/**
 * Live Chat Component Styles
 *
 * Uses SDS (System Design System) tokens for visual consistency
 * with the rest of the WOW application.
 * Buttons are composed using <wow-button> for automatic consistency.
 */

import { css } from "lit";

export const liveChatStyles = css`
  :host {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 200px;
    background: var(--sds-color-background-default-secondary);
    border-radius: var(--sds-size-radius-400);
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    overflow: hidden;
  }

  .chat-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* Header */
  .chat-header {
    padding: var(--sds-size-space-400) 12px;
    border-bottom: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    font-weight: var(--sds-typography-weight-semibold);
    font-family: var(--sds-typography-family-title);
    color: var(--sds-color-text-default-default);
  }

  /* Loading state */
  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--sds-color-text-default-secondary);
  }

  .loading-spinner {
    width: var(--sds-size-icon-small);
    height: var(--sds-size-icon-small);
    border: var(--sds-size-space-100) solid var(--sds-color-text-default-tertiary);
    border-top-color: transparent;
    border-radius: var(--sds-size-radius-full);
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Error state */
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: var(--sds-size-space-600);
    text-align: center;
    color: var(--sds-color-text-danger-default);
  }

  .error-container wow-button {
    margin-top: var(--sds-size-space-400);
  }

  /* Empty state */
  .empty-container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--sds-color-text-default-secondary);
    padding: var(--sds-size-space-600);
    text-align: center;
    font-size: var(--sds-typography-body-size-medium);
  }

  /* Waiting for event state */
  .waiting-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: var(--sds-size-space-600);
    text-align: center;
  }

  .waiting-icon {
    color: var(--sds-color-text-default-secondary);
    margin-bottom: var(--sds-size-space-400);
  }

  .waiting-message {
    color: var(--sds-color-text-default-secondary);
    font-size: var(--sds-typography-body-size-medium);
    margin: 0;
  }

  /* Messages list */
  .messages-container {
    flex: 1;
    overflow-y: auto;
    padding: var(--sds-size-space-300) 0;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .message {
    display: flex;
    gap: 12px;
    padding: var(--sds-size-space-400) 12px;
    transition: background-color 0.15s ease;
  }

  .message:hover {
    background: var(--sds-color-background-default-tertiary);
  }

  .message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--sds-color-background-default-tertiary);
    flex-shrink: 0;
    overflow: hidden;
  }

  .message-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--sds-color-brand-default), var(--sds-color-accent-default));
    color: var(--sds-color-white-1000);
    font-size: var(--sds-typography-body-size-small);
    font-weight: var(--sds-typography-weight-semibold);
    border: 2px solid var(--sds-color-border-default-secondary);
    box-sizing: border-box;
  }

  .message-content {
    flex: 1;
    min-width: 0;
  }

  .message-header {
    display: flex;
    align-items: baseline;
    gap: var(--sds-size-space-300);
    margin-bottom: var(--sds-size-space-100);
  }

  .message-author {
    font-family: var(--sds-typography-family-title);
    font-weight: var(--sds-typography-weight-medium);
    color: var(--sds-color-text-default-default);
    font-size: var(--sds-typography-body-size-large);
  }

  .message-time {
    font-size: var(--sds-typography-body-size-small);
    color: var(--sds-color-text-default-secondary);
  }

  .message-text {
    color: var(--sds-color-text-default-default);
    font-size: var(--sds-typography-body-size-medium);
    line-height: 1.4;
    word-break: normal;
  }

  /* Input area */
  .input-container {
    flex-shrink: 0;
    padding: var(--sds-size-space-400) 12px;
    padding-bottom: calc(var(--sds-size-space-400) + env(safe-area-inset-bottom, 0px));
    border-top: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    background: var(--sds-color-background-default-tertiary);
  }

  .input-wrapper {
    display: flex;
    gap: var(--sds-size-space-300);
    align-items: flex-start;
  }

  .input-wrapper wow-button[square] {
    flex-shrink: 0;
    margin-top: var(--sds-size-space-400);
    padding-top: 1px;
  }

  .message-input {
    flex: 1;
    min-width: 0;
  }

  /* Emoji picker */
  .emoji-picker {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sds-size-space-150);
    padding: var(--sds-size-space-300);
    background: var(--sds-color-background-default-default);
    border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
    border-radius: var(--sds-size-radius-400);
    margin-bottom: var(--sds-size-space-300);
  }

  .emoji-picker.hidden {
    display: none;
  }

  .emoji-picker wow-button {
    font-size: 20px;
  }

  /* Login prompt */
  .login-prompt {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sds-size-space-300);
    padding: var(--sds-size-space-600);
    text-align: center;
    color: var(--sds-color-text-default-secondary);
    font-size: var(--sds-typography-body-size-medium);
  }

  /* Mobile portrait: reduce spacing */
  @media (max-width: 768px) {
    :host {
      min-height: 150px;
      border-radius: 0;
      border-left: none;
      border-right: none;
    }

    .chat-header {
      padding: 8px 12px;
      font-size: 14px;
    }

    .messages-container {
      padding: 4px 0;
    }

    .message {
      padding: 6px 12px;
      gap: 8px;
    }

    .message-avatar {
      width: 28px;
      height: 28px;
    }

    .message-author {
      font-size: 14px;
    }

    .message-text {
      font-size: 14px;
    }

    .input-container {
      padding: 8px 12px;
    }

    .input-wrapper {
      gap: 8px;
    }
  }

  /* Mobile landscape: minimal spacing for maximum content */
  @media (max-height: 500px) and (orientation: landscape) {
    :host {
      min-height: auto;
      border: none;
      border-radius: 0;
    }

    .chat-header {
      padding: 4px 8px;
      font-size: 12px;
    }

    .messages-container {
      padding: 2px 0;
    }

    .message {
      padding: 4px 8px;
      gap: 6px;
    }

    .message-avatar {
      width: 24px;
      height: 24px;
    }

    .message-header {
      margin-bottom: 0;
      gap: 4px;
    }

    .message-author {
      font-size: 12px;
    }

    .message-time {
      font-size: 10px;
    }

    .message-text {
      font-size: 12px;
      line-height: 1.3;
    }

    .input-container {
      padding: 4px 8px;
    }

    .input-wrapper {
      gap: 4px;
    }

    .input-wrapper wow-button[square] {
      margin-top: 0;
    }

    .empty-container,
    .waiting-container,
    .error-container {
      padding: 8px;
      font-size: 12px;
    }

    .login-prompt {
      padding: 8px;
      gap: 8px;
      font-size: 12px;
    }
  }
`;
